import { Component, OnInit, ContentChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';

import Task = gapi.client.tasks.Task;

import { SubtasksComponent } from '../subtasks/subtasks.component';
import { StoreService } from '../store.service';
import { TasksGetParams, TasksPatchParams } from '../api/tasks-resource-method-params';

const DEBUG = true;
const DATE_REGEX = Validators.pattern(/\d{4}-\d{2}-\d{2}/); // yyyy-mm-dd

  /**
   * The form update policy is on change so that edits to the title or due date
   * are immediately reflected in the corresponding list view.
   *
   * However, to avoid over-frequent network calls and screen redraws,
   * the model is only updated when an edited field loses focus.
   */
@Component({
  selector: 'app-task-details',
  templateUrl: './task-details.component.html',
  styleUrls: ['./task-details.component.scss']
})
export class TaskDetailsComponent implements OnInit {

  @ContentChild('app-subtasks') subtasks!: SubtasksComponent;

  readonly form = this.fb.group({
    title: ['', Validators.required], // eslint-disable-line @typescript-eslint/unbound-method
    notes: [''],
    due: ['', DATE_REGEX],
    updated: this.fb.group({
      date: {value: ['', DATE_REGEX], diabled: true},
      time: {value: [''], diabled: true}
    }),
    completed: this.fb.group({
      date: {value: ['', DATE_REGEX], diabled: true},
      time: {value: [''], diabled: true}
    }),
    deleted: {value: false, disabled: true}
  },
  {
    updateOn: 'change'
  });

  formChanges?: Subscription;
  titleChanges?: Subscription;
  notesChanges?: Subscription;
  dueChanges?: Subscription;

  task?: Task;
  fullTask?: Task;

  constructor(private store: StoreService, private fb: FormBuilder) {
    if (DEBUG) {console.log('new TaskDetailsComponent()');}
    this.form.disable();
  }

  ngOnInit(): void {

    this.store.focus$.subscribe((task: Task|null) => {
      if (DEBUG) {console.log('New focus:', task);}
      /* Unsubscribe previous Task from any valueChanges */
      if (this.formChanges)  {this.formChanges.unsubscribe();}
      if (this.titleChanges) {this.titleChanges.unsubscribe();}
      if (this.notesChanges) {this.notesChanges.unsubscribe();}
      if (this.dueChanges) {this.dueChanges.unsubscribe();}

      if (DEBUG) {console.log('Form reset.');}
      this.form.reset();

      if (!task) {
        /* Disable the form */
        this.form.disable({emitEvent:false});
        this.task = undefined;
      }
      else {
        // sugar
        const title = this.form.controls.title;
        const notes = this.form.controls.notes;
        const due = this.form.controls.due;

        this.task = task;

        /* Bind values, subscribe to changes, and finally enable the form */
        title.setValue(task.title);
        this.titleChanges = title.valueChanges.subscribe((value) => {
          if (title.dirty) {
            if (DEBUG) {console.log('Title change:', value);}
            task.title = title.value;
          }
          if (title.touched) {
            if (DEBUG) {console.log('Title touched:', title)}
            title.markAsPristine();
            // title.markAsUntouched();
          }
        });

        if (task.notes) {
          notes.setValue(task.notes);
        }
        this.notesChanges = notes.valueChanges.subscribe((value) => {
          if (notes.dirty) {
            if (DEBUG) {console.log('Notes change:', value);}
            task.notes = notes.value;
          }
          if (notes.touched) {
            if (DEBUG) {console.log('Notes touched:', notes)}
            notes.markAsPristine();
            // notes.markAsUntouched();
          }
        });

        if (task.due) {
          due.setValue(new Date(task.due).toISOString().split('T')[0]);
        }
        this.dueChanges = due.valueChanges.subscribe((value) => {
          if (due.dirty) {
            if (DEBUG) {console.log('Due date change:', value);}
            task.due = new Date(due.value).toISOString();
          }
          if (due.touched) {
            if (DEBUG) {console.log('Due touched:', due)}
            due.markAsPristine();
            // due.markAsUntouched();
          }
        });

        if (task.completed) {
          const [aDate, aTime] = new Date(task.completed).toISOString().split('T');
          this.form.controls.completed.setValue({ date: aDate, time: aTime.slice(0, 8) });
        }
        else {this.form.controls.completed.reset();}

        if (task.updated) {
          const [aDate, aTime] = new Date(task.updated).toISOString().split('T');
          this.form.controls.updated.setValue({ date: aDate, time: aTime.substr(0, 8) });
        }
        else {this.form.controls.updated.reset();}

        if (task.deleted) {
          this.form.controls.deleted.setValue('true');
        }
        else {
          this.form.controls.deleted.reset();
        }

        this.form.enable({emitEvent:false});
        this.form.controls.deleted.disable({emitEvent:false});

        const req: TasksGetParams = {
          /* eslint-disable @typescript-eslint/no-non-null-assertion */
          task: task.id!,
          tasklist: this.store.taskList!.id!
          /* eslint-enable @typescript-eslint/no-non-null-assertion */
        };
        void this.store.getTask(req).then(task => {
          this.fullTask = task;
        });
      }
    });
  }

  onBlur(): void {
    if (this.task && this.form.dirty) {
      if (DEBUG) {console.log('TaskDetailsComponent onBlur()')}
      const update: Task = {};
      if (this.form.controls.title.dirty) {
        if (DEBUG) {console.log('requesting title change')}
        update.title = this.task.title;
      }
      if (this.form.controls.notes.dirty) {
        if (DEBUG) {console.log('requesting notes change')}
        update.notes = this.task.notes;
      }
      if (this.form.controls.due.dirty) {
        if (DEBUG) {console.log('requesting due-date change')}
        update.due = this.task.due;
      }
      const request: TasksPatchParams = {
        /* eslint-disable @typescript-eslint/no-non-null-assertion */
        tasklist: this.store.taskList!.id!,
        task: this.task.id!,
        resource: update
        /* eslint-enable @typescript-eslint/no-non-null-assertion */
      };
      if (this.task.parent) {
        if (DEBUG) {console.log('calling onStepUpdated()', request);}
        void this.store.onStepUpdated(request);
      }
      else {
        if (DEBUG) {console.log('calling onTaskUpdated()', request);}
        void this.store.onTaskUpdated(request);
      }
    }
  }

  setRecurrence(): void {
    console.error('TODO: Implement setRecurring()');
  }
}
